import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute, Outlet, retainSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { CarFront } from 'lucide-react';
import type { ReactNode } from 'react';
import { portalScopeSearchSchema } from './portal-scope-search';
import { PortalScopeProvider } from './usePortalScope';

export const Route = createFileRoute('/console/$orgId/fleet-portal')({
    staticData: {
        modules: ['fleet'],
        permissions: ['fleet_portal:access'],
        ui: {
            title: 'Fleet portal',
            icon: CarFront,
        },
        breadcrumb: {
            title: 'Fleet portal',
        },
    },
    // The client scope lives on the layout (CUR-1100): one search param for
    // every portal page, and retainSearchParams keeps the selection across the
    // sidebar, tiles and back links, which navigate with `to` alone.
    validateSearch: zodValidator(portalScopeSearchSchema),
    search: { middlewares: [retainSearchParams(['fleetClientId'])] },
    // Warm the one query every portal page reads through PortalScopeProvider.
    // This option is also load-bearing for typing: with a search middleware
    // declared, TanStack only infers the search schema correctly when a
    // context-sensitive option such as beforeLoad is present.
    beforeLoad: ({ context: { trpcUtils } }): ShareableRouteContext => {
        // A console user who types a portal URL is refused here and shown Not
        // found by the route guard; the provider surfaces any error itself.
        void trpcUtils.fleetPortal.getPortalContext.ensureData().catch(() => undefined);
        return {};
    },
    component: FleetPortalLayout,
});

function FleetPortalLayout(): ReactNode {
    const { fleetClientId } = Route.useSearch();
    return (
        <PortalScopeProvider fleetClientIdSearch={fleetClientId}>
            <Outlet />
        </PortalScopeProvider>
    );
}
